import React, { useState, useEffect } from 'react';

const LoadingSpinner: React.FC = () => {
  const [currentMessage, setCurrentMessage] = useState(0);

  const messages = [
    "Gathering components from the digital realm...",
    "Assembling the PatternLab universe...",
    "Brewing some design magic...",
    "Connecting the design dots...",
    "Loading awesomeness...",
    "Preparing your creative journey...",
    "Spinning up the design engine...",
    "Almost there, keep the faith! ✨"
  ];

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % messages.length);
    }, 3000);

    return () => {
      clearInterval(messageInterval);
    };
  }, [messages.length]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-8 p-8">
      {/* Fun Animated Logo */}
      <div className="relative">
        {/* Main spinning circle with gradient */}
        <div className="w-20 h-20 border-4 border-transparent rounded-full animate-spin"
             style={{
               background: 'linear-gradient(45deg, #3B82F6, #8B5CF6, #EC4899, #F59E0B)',
               backgroundSize: '400% 400%'
             }}>
        </div>
        
        {/* Floating particles around the spinner */}
        <div className="absolute inset-0">
          <div className="absolute -top-2 -left-2 w-3 h-3 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
          <div className="absolute -top-2 -right-2 w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0.6s' }}></div>
        </div>
        
        {/* Inner pulsing circle */}
        <div className="absolute inset-2 bg-white dark:bg-gray-800 rounded-full animate-ping opacity-20"></div>
      </div>

      {/* Bouncing PatternLab Text */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-pulse">
          PatternLab
        </h1>
        <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-300">
          Dashboard
        </h2>
      </div>

      {/* Fun Loading Message */}
      <div className="text-center max-w-md">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-700">
          <p className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">
            {messages[currentMessage]}
          </p>
          <div className="flex justify-center space-x-1">
            <span className="text-2xl text-blue-500 animate-bounce" style={{ animationDelay: '0s' }}>⚡</span>
            <span className="text-2xl text-purple-500 animate-bounce" style={{ animationDelay: '0.1s' }}>🎨</span>
            <span className="text-2xl text-pink-500 animate-bounce" style={{ animationDelay: '0.2s' }}>✨</span>
            <span className="text-2xl text-yellow-500 animate-bounce" style={{ animationDelay: '0.3s' }}>🚀</span>
          </div>
        </div>
      </div>

      {/* Animated Progress Bar */}
      <div className="w-64 bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full animate-pulse"
             style={{
               width: '60%'
             }}>
        </div>
      </div>

      {/* Fun Fact or Tip */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 max-w-md">
        <p className="animate-pulse">
          💡 <span className="font-medium">Pro tip:</span> While you wait, why not practice your design thinking?
        </p>
      </div>

      {/* Additional Fun Elements */}
      <div className="flex space-x-4">
        <div className="w-4 h-4 bg-blue-400 rounded-full animate-ping"></div>
        <div className="w-4 h-4 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
        <div className="w-4 h-4 bg-pink-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
        <div className="w-4 h-4 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '1.5s' }}></div>
      </div>
    </div>
  );
};

export default LoadingSpinner;
